import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useSeo } from '../hooks/useSeo';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  ButtonGroup,
  Badge,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from 'reactstrap';
import { FaTable, FaTh, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { getNewPatterns } from '../actions/patterns';
import { PatternGrid, PatternTable } from './PatternCard';
import './ClassicPatterns.css';
import { getTickerType, TickerType } from '../utils/typeValidation';
import { PATTERN_INFO, PATTERN_TYPES, PATTERN_CATEGORIES, PATTERNS_BY_CATEGORY, COMMON_PATTERN_TYPES } from '../utils/patternInfo';
import { PatternSparkline } from './PatternSparkline';
import { TableRowSkeleton } from './SkeletonLoader';
import { useFundamentals } from '../hooks/useFundamentals';

// 'all' fetches every window in one response (GET /api/patterns/new?window=all) instead
// of the usual single-window filter — the backend already scores/detects per-window, this
// just skips the window == filter (see stocks_api resources/patterns.py _resolve_window_param).
const WINDOW_OPTIONS: { value: PatternWindow; label: string; shortLabel: string }[] = [
  { value: 22,  label: '1 Month',    shortLabel: '1M'  },
  { value: 45,  label: '2 Months',   shortLabel: '2M'  },
  { value: 65,  label: '3 Months',   shortLabel: '3M'  },
  { value: 130, label: '6 Months',   shortLabel: '6M'  },
  { value: 195, label: '9 Months',   shortLabel: '9M'  },
  { value: 260, label: '1 Year',     shortLabel: '1Y'  },
  { value: 'all', label: 'All Windows', shortLabel: 'All' },
];

// A single window's own recent-pattern count comfortably clears 2000 (the pre-existing
// fetch limit) on live data. 'all' sums every window into one response, so it needs
// materially more headroom — measured against a real dataset, six windows' worth of a
// single week can run to ~2-3x a single window's own count.
const DEFAULT_FETCH_LIMIT = 2000;
const ALL_WINDOWS_FETCH_LIMIT = 6000;



export function ClassicPatterns() {
  useSeo({
    title: "Classic Chart Patterns — Head & Shoulders, Flags, Double Bottoms",
    description: "Template-matched classic chart patterns across Russell 3000 stocks — head & shoulders, flags, wedges, double tops/bottoms and more, with confidence scores and historical context.",
    path: "/patterns",
  });
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const patternsState = useSelector((state: RootReducerState) => state.patterns);
  const authState = useSelector((state: RootReducerState) => state.authentication);
  const [searchParams, setSearchParams] = useSearchParams();
  const getFundamentals = useFundamentals();

  const isAnonymous = !authState.loggedIn;

  // Initialize state from URL params
  const sentimentParam = searchParams.get('sentiment');
  const patternTypesParam = searchParams.get('types');
  const tickerTypeParam = searchParams.get('tickerType');
  const sectorsParam = searchParams.get('sectors');
  const windowParam = searchParams.get('window');
  const initialSentiment: 'all' | 'bullish' | 'bearish' =
    sentimentParam === 'bullish' || sentimentParam === 'bearish' ? sentimentParam : 'all';
  const initialPatternTypes: Set<string> = new Set(
    patternTypesParam ? patternTypesParam.split(',').filter(t => PATTERN_TYPES.some(pt => pt.value === t)) : []
  );
  const initialTickerType: TickerType | 'all' =
    tickerTypeParam === 'stock' || tickerTypeParam === 'index' || tickerTypeParam === 'crypto' || tickerTypeParam === 'futures'
      ? tickerTypeParam
      : 'all';
  const initialSectors: Set<string> = new Set(sectorsParam ? sectorsParam.split(',') : []);
  const initialWindow: PatternWindow =
    windowParam === 'all'
      ? 'all'
      : windowParam && WINDOW_OPTIONS.some(w => w.value === Number(windowParam))
        ? Number(windowParam)
        : 22;

  const [loadedViewMode, setLoadedViewMode] = useState<'new' | null>(null);
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'bullish' | 'bearish'>(initialSentiment);
  const [selectedPatternTypes, setSelectedPatternTypes] = useState<Set<string>>(initialPatternTypes);
  const [tickerTypeFilter, setTickerTypeFilter] = useState<TickerType | 'all'>(initialTickerType);
  const [selectedSectors, setSelectedSectors] = useState<Set<string>>(initialSectors);
  const [patternDropdownOpen, setPatternDropdownOpen] = useState(false);
  const [sectorDropdownOpen, setSectorDropdownOpen] = useState(false);
  const [showAllPatternTypes, setShowAllPatternTypes] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(7);
  const [patternWindow, setPatternWindow] = useState<PatternWindow>(initialWindow);
  // Display mode: 'table' for desktop, 'cards' for mobile by default
  const [displayMode, setDisplayMode] = useState<'table' | 'cards'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'
  );

  const togglePatternType = (type: string) => {
    setSelectedPatternTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleSector = (sector: string) => {
    setSelectedSectors(prev => {
      const next = new Set(prev);
      if (next.has(sector)) {
        next.delete(sector);
      } else {
        next.add(sector);
      }
      return next;
    });
  };

  const handleSentimentFilter = (value: 'all' | 'bullish' | 'bearish') => {
    setSentimentFilter(value);
    posthog.capture("classic_patterns_filtered", { filter_type: "sentiment", value });
  };

  const handleWindowChange = (value: PatternWindow) => {
    setPatternWindow(value);
    posthog.capture("classic_patterns_filtered", { filter_type: "window", value });
  };

  const handleLookbackChange = (value: number) => {
    setLookbackDays(value);
    posthog.capture("classic_patterns_filtered", { filter_type: "lookback_days", value });
  };

  // Sync URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (sentimentFilter !== 'all') {
      params.set('sentiment', sentimentFilter);
    }
    if (selectedPatternTypes.size > 0) {
      params.set('types', Array.from(selectedPatternTypes).join(','));
    }
    if (tickerTypeFilter !== 'all') {
      params.set('tickerType', tickerTypeFilter);
    }
    if (selectedSectors.size > 0) {
      params.set('sectors', Array.from(selectedSectors).join(','));
    }
    if (patternWindow !== 22) {
      params.set('window', patternWindow.toString());
    }
    params.set('lookback_days', lookbackDays.toString());
    setSearchParams(params, { replace: true });
  }, [sentimentFilter, selectedPatternTypes, tickerTypeFilter, selectedSectors, patternWindow, lookbackDays, setSearchParams]);

  // Load initial data
  useEffect(() => {
    setLoadedViewMode(null);
    const fetchData = async () => {
      const limit = patternWindow === 'all' ? ALL_WINDOWS_FETCH_LIMIT : DEFAULT_FETCH_LIMIT;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await dispatch(getNewPatterns(lookbackDays, 0, limit, patternWindow) as any);
      setLoadedViewMode('new');
    };
    fetchData();
  }, [dispatch, lookbackDays, patternWindow]);

  useEffect(() => {
    posthog.capture("pattern_list_viewed", { lookback_days: lookbackDays, window: patternWindow });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sector for a ticker, normalized so unknowns (crypto/futures/forex, or anything
  // missing from fundamentals.pkl) group under one consistent bucket for filtering/sorting.
  const sectorFor = (ticker: string) => getFundamentals(ticker)?.sector || 'Other';

  // Filter patterns by sentiment, pattern type, ticker type, and sector
  const filterPatterns = (patterns: PatternMatch[] | undefined) => {
    if (!patterns) return [];
    let filtered = patterns;
    if (sentimentFilter !== 'all') {
      filtered = filtered.filter(p => p.sentiment === sentimentFilter);
    }
    if (selectedPatternTypes.size > 0) {
      filtered = filtered.filter(p => selectedPatternTypes.has(p.pattern_type));
    }
    if (tickerTypeFilter !== 'all') {
      filtered = filtered.filter(p => getTickerType(p.ticker) === tickerTypeFilter);
    }
    if (selectedSectors.size > 0) {
      filtered = filtered.filter(p => selectedSectors.has(sectorFor(p.ticker)));
    }
    return filtered;
  };

  const filteredPatterns = filterPatterns(patternsState.newPatterns);

  // Sectors actually present in the currently loaded patterns, for the filter dropdown —
  // derived rather than a hardcoded GICS list so it always matches what's on screen.
  const availableSectors = Array.from(
    new Set((patternsState.newPatterns ?? []).map(p => sectorFor(p.ticker)))
  ).sort((a, b) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)));

  const isLoading = patternsState.loading || loadedViewMode !== 'new';

  // Get summary stats, accounting for pattern type, ticker type, and sector filters
  let typeFiltered = selectedPatternTypes.size > 0
    ? patternsState.newPatterns?.filter(p => selectedPatternTypes.has(p.pattern_type))
    : patternsState.newPatterns;
  if (tickerTypeFilter !== 'all') {
    typeFiltered = typeFiltered?.filter(p => getTickerType(p.ticker) === tickerTypeFilter);
  }
  if (selectedSectors.size > 0) {
    typeFiltered = typeFiltered?.filter(p => selectedSectors.has(sectorFor(p.ticker)));
  }
  const bullishCount = typeFiltered?.filter(p => p.sentiment === 'bullish').length || 0;
  const bearishCount = typeFiltered?.filter(p => p.sentiment === 'bearish').length || 0;
  const totalCount = typeFiltered?.length || 0;

  return (
    <Container className="classic-patterns-container">
      <div className="page-header patterns-title">
        <h1 className="page-title">Classic Chart Patterns</h1>
        <Badge color="primary" className="new-count-badge">
          {totalCount} New {lookbackDays === 1 ? 'Latest' : 'This Week'} · {WINDOW_OPTIONS.find(w => w.value === patternWindow)?.label}
        </Badge>
      </div>

      <div className="filter-controls mb-3">
        <div className="patterns-header">
          <div className="patterns-controls">
            {/* Time Range */}
            <ButtonGroup size="sm">
              <Button
                color={lookbackDays === 1 ? 'primary' : 'secondary'}
                onClick={() => handleLookbackChange(1)}
                outline={lookbackDays !== 1}
              >
                Latest
              </Button>
              <Button
                color={lookbackDays === 7 ? 'primary' : 'secondary'}
                onClick={() => handleLookbackChange(7)}
                outline={lookbackDays !== 7}
              >
                This Week
              </Button>
            </ButtonGroup>

            {/* Pattern Length */}
            <ButtonGroup size="sm">
              {/* Every window is available to every audience since 2026-08-11 —
                  window depth is no longer a paid axis. */}
              {WINDOW_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  color={patternWindow === opt.value ? 'primary' : 'secondary'}
                  outline={patternWindow !== opt.value}
                  onClick={() => handleWindowChange(opt.value)}
                >
                  {opt.shortLabel}
                </Button>
              ))}
            </ButtonGroup>

            {/* Sentiment Filter */}
            <ButtonGroup size="sm">
              <Button
                color={sentimentFilter === 'all' ? 'success' : 'secondary'}
                onClick={() => handleSentimentFilter('all')}
                outline={sentimentFilter !== 'all'}
              >
                All ({totalCount})
              </Button>
              <Button
                color={sentimentFilter === 'bullish' ? 'success' : 'secondary'}
                onClick={() => handleSentimentFilter('bullish')}
                outline={sentimentFilter !== 'bullish'}
              >
                Bullish ({bullishCount})
              </Button>
              <Button
                color={sentimentFilter === 'bearish' ? 'danger' : 'secondary'}
                onClick={() => handleSentimentFilter('bearish')}
                outline={sentimentFilter !== 'bearish'}
              >
                Bearish ({bearishCount})
              </Button>
            </ButtonGroup>

            {/* Ticker Type Filter */}
            <ButtonGroup size="sm">
              <Button
                color={tickerTypeFilter === 'all' ? 'primary' : 'secondary'}
                onClick={() => setTickerTypeFilter('all')}
                outline={tickerTypeFilter !== 'all'}
              >
                All Types
              </Button>
              <Button
                color={tickerTypeFilter === 'stock' ? 'primary' : 'secondary'}
                onClick={() => setTickerTypeFilter('stock')}
                outline={tickerTypeFilter !== 'stock'}
              >
                Stocks/ETFs
              </Button>
              <Button
                color={tickerTypeFilter === 'index' ? 'primary' : 'secondary'}
                onClick={() => setTickerTypeFilter('index')}
                outline={tickerTypeFilter !== 'index'}
              >
                Indexes
              </Button>
              <Button
                color={tickerTypeFilter === 'crypto' ? 'primary' : 'secondary'}
                onClick={() => setTickerTypeFilter('crypto')}
                outline={tickerTypeFilter !== 'crypto'}
              >
                Crypto & Forex
              </Button>
              <Button
                color={tickerTypeFilter === 'futures' ? 'primary' : 'secondary'}
                onClick={() => setTickerTypeFilter('futures')}
                outline={tickerTypeFilter !== 'futures'}
              >
                Futures
              </Button>
            </ButtonGroup>

            {/* Pattern Type Filter */}
            <Dropdown
              isOpen={patternDropdownOpen}
              toggle={() => setPatternDropdownOpen(prev => !prev)}
              size="sm"
            >
              <DropdownToggle caret outline color={selectedPatternTypes.size > 0 ? 'primary' : 'secondary'}>
                <FaFilter style={{ marginRight: 4 }} />
                {selectedPatternTypes.size === 0
                  ? 'All Types'
                  : `${selectedPatternTypes.size} Type${selectedPatternTypes.size > 1 ? 's' : ''}`}
              </DropdownToggle>
              <DropdownMenu className="pattern-type-dropdown">
                {PATTERN_CATEGORIES.map(category => {
                  const typesInCategory = PATTERNS_BY_CATEGORY[category].filter(
                    pt => showAllPatternTypes || COMMON_PATTERN_TYPES.includes(pt.value)
                  );
                  if (typesInCategory.length === 0) return null;
                  return (
                    <div key={category} className="pattern-type-group">
                      <div className="pattern-type-group-header">{category}</div>
                      {typesInCategory.map(pt => (
                        <label
                          key={pt.value}
                          className="pattern-type-option"
                          onClick={e => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPatternTypes.has(pt.value)}
                            onChange={() => togglePatternType(pt.value)}
                          />
                          <span>{pt.label}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
                <div className="dropdown-divider" />
                <button
                  className="pattern-type-clear"
                  onClick={e => { e.stopPropagation(); setShowAllPatternTypes(prev => !prev); }}
                >
                  {showAllPatternTypes ? 'Show common types only' : `Show all ${PATTERN_TYPES.length} types`}
                </button>
                {selectedPatternTypes.size > 0 && (
                  <button
                    className="pattern-type-clear"
                    onClick={e => { e.stopPropagation(); setSelectedPatternTypes(new Set()); }}
                  >
                    Clear all
                  </button>
                )}
              </DropdownMenu>
            </Dropdown>

            {/* Sector Filter */}
            <Dropdown
              isOpen={sectorDropdownOpen}
              toggle={() => setSectorDropdownOpen(prev => !prev)}
              size="sm"
            >
              <DropdownToggle caret outline color={selectedSectors.size > 0 ? 'primary' : 'secondary'}>
                <FaFilter style={{ marginRight: 4 }} />
                {selectedSectors.size === 0
                  ? 'All Sectors'
                  : `${selectedSectors.size} Sector${selectedSectors.size > 1 ? 's' : ''}`}
              </DropdownToggle>
              <DropdownMenu className="pattern-type-dropdown">
                {availableSectors.map(sector => (
                  <label
                    key={sector}
                    className="pattern-type-option"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSectors.has(sector)}
                      onChange={() => toggleSector(sector)}
                    />
                    <span>{sector}</span>
                  </label>
                ))}
                {selectedSectors.size > 0 && (
                  <>
                    <div className="dropdown-divider" />
                    <button
                      className="pattern-type-clear"
                      onClick={e => { e.stopPropagation(); setSelectedSectors(new Set()); }}
                    >
                      Clear all
                    </button>
                  </>
                )}
              </DropdownMenu>
            </Dropdown>

            {/* Display Mode Toggle */}
            <ButtonGroup size="sm" className="display-mode-toggle">
              <Button
                color={displayMode === 'table' ? 'dark' : 'secondary'}
                onClick={() => setDisplayMode('table')}
                outline={displayMode !== 'table'}
                title="Table view"
              >
                <FaTable />
              </Button>
              <Button
                color={displayMode === 'cards' ? 'dark' : 'secondary'}
                onClick={() => setDisplayMode('cards')}
                outline={displayMode !== 'cards'}
                title="Card view"
              >
                <FaTh />
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>

      {isAnonymous && (
        <Alert color="info" className="d-flex flex-wrap align-items-center justify-content-between">
          <span>
            <FaCalendarAlt className="mr-1" /> Tickers beyond the top match in each pattern are hidden
            until you sign up. A <strong>free</strong> account reveals every ticker, every window.
          </span>
          <Button color="primary" size="sm" href="/signup" className="mt-2 mt-md-0">
            Create a free account →
          </Button>
        </Alert>
      )}
      {isLoading ? (
        <div className="classic-patterns-loading">
          <TableRowSkeleton rows={8} />
        </div>
      ) : patternsState.errorMessage ? (
        <div className="classic-patterns-error">
          <p>Error loading patterns: {patternsState.errorMessage}</p>
        </div>
      ) : filteredPatterns.length === 0 ? (
        <div className="no-patterns-message">
          <p>No {sentimentFilter !== 'all' ? sentimentFilter : ''} patterns found {lookbackDays === 1 ? 'in the latest run' : 'this week'}.</p>
          <p className="text-muted">
            Check back later or try adjusting the filters.
          </p>
        </div>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <small className="text-muted">
                Showing {filteredPatterns.length} pattern{filteredPatterns.length !== 1 ? 's' : ''} detected using proprietary template matching
              </small>
            </Col>
          </Row>
          {displayMode === 'table' ? (
            <PatternTable
              patterns={filteredPatterns}
              showNewBadge={true}
              isAnonymous={isAnonymous}
              lookbackDays={lookbackDays}
            />
          ) : (
            <PatternGrid
              patterns={filteredPatterns}
              showNewBadge={true}
              isAnonymous={isAnonymous}
              lookbackDays={lookbackDays}
            />
          )}
        </>
      )}

      {/* Pattern Types Legend */}
      <Card className="patterns-legend">
        <CardBody>
          <h5>Pattern Guide</h5>
          <Row>
            {PATTERN_CATEGORIES.map(category => (
              <Col md={4} key={category}>
                <strong>{category}</strong>
                <ul className="pattern-list">
                  {PATTERNS_BY_CATEGORY[category].map(({ value: key, label }) => (
                    <li key={key} className="pattern-legend-item">
                      <PatternSparkline patternType={key} width={64} height={30} className="pattern-legend-sparkline" />
                      <div className="pattern-legend-text">
                        <span className="pattern-legend-name">{label}</span>
                        <span className="pattern-legend-desc">{PATTERN_INFO[key].summary}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </Col>
            ))}
          </Row>
          <p className="text-muted mt-2">
            <small>
                Patterns are detected by comparing price movements against idealized templates
                across multiple timeframes (22, 44, 66, 166, 189 days).
            </small>
          </p>
        </CardBody>
      </Card>
    </Container>
  );
}
